/**
 * Relay-bootstrap pairing (task-15) — client side (Lead decision A).
 *
 * After the phone is paired with the Worker (device token + relay URL) it still
 * has no E2E channel key with the PC daemon: the daemon's X25519 public key is
 * not part of the Worker pairing response. This module runs a one-time
 * bootstrap over the relay:
 *
 *   1. phone sends a **plaintext** `pair.begin.request` envelope to the daemon
 *      (public keys, request id, device name/platform, timestamp only — the E2E
 *      channel does not exist yet; no token/private key is ever included);
 *   2. the user approves it locally on the PC (`approve-relay-pairing.ts`
 *      --approve <id>; --identity prints the daemon id/public key);
 *   3. the daemon answers `pair.begin.response` with `accepted`,
 *      `daemon_public_key` (raw X25519 base64url) and `daemon_device_id`;
 *   4. the app saves the daemon keys, derives the channel key, live relay works.
 *
 * Everything is framework-free and injectable (`webSocketFactory`, `scheduler`,
 * `clock`, `requestIdFactory`) so the whole handshake is tested offline against
 * a FakeWebSocket. No secret is written to state/logs.
 */
import type { Envelope, EventPayload, MessageType } from '@luminis/protocol';
import { makeEnvelope } from '@luminis/protocol';
import {
  RelayClient,
  type MessageFrame,
  type RelayClientError,
  type RelayClientOptions,
  type RelayStatus,
  type RelayTimers,
} from '../api/relay';
import { randomId } from '../id';
import { normalizeDaemonDeviceId, normalizeDaemonE2ePublicKey } from '../live/plan';
import type { DeviceIdentityStored } from '../types';

export const RELAY_PAIRING_REQUEST_TYPE: MessageType = 'pair.begin.request';
export const RELAY_PAIRING_RESPONSE_TYPE: MessageType = 'pair.begin.response';
/** Manual PC approval can take a while; keep the pending window generous. */
export const RELAY_PAIRING_DEFAULT_TIMEOUT_MS = 300_000;
/** Max accepted/denied reason length shown in the UI. */
const MAX_REASON_LENGTH = 300;

// ---------------------------------------------------------------------------
// Contract payloads
// ---------------------------------------------------------------------------

/** Plaintext bootstrap request payload (public values only). */
export interface RelayPairingRequestPayload {
  request_id: string;
  /** Phone X25519 public key (raw base64url, 32 bytes). */
  public_key: string;
  /** Phone Ed25519 approval-verification public key (raw base64url). */
  signing_public_key: string;
  device_name?: string;
  platform?: string;
  /** Unix seconds. */
  requested_at: number;
}

export interface RelayPairingAccepted {
  daemon_device_id: string;
  daemon_public_key: string;
  accepted_at: number;
}

export type RelayPairingStatus =
  'idle' | 'connecting' | 'waiting' | 'accepted' | 'denied' | 'timeout' | 'disconnected' | 'error';

export interface RelayPairingView {
  status: RelayPairingStatus;
  request_id?: string;
  /** Human-readable pending explanation (never contains secrets). */
  message?: string;
  error?: string;
  /** Whether the UI should offer a retry button. */
  retryable: boolean;
  daemon_device_id?: string;
  daemon_public_key?: string;
  accepted_at?: number;
}

export interface BuildPairingRequestOptions {
  identity: DeviceIdentityStored;
  requestId: string;
  now?: () => number;
  deviceName?: string;
  platform?: string;
}

/**
 * Build the bootstrap request. Only public keys/name/platform/timestamp are
 * included; tests assert that no token/private key material leaks into it.
 */
export function buildRelayPairingRequest(
  options: BuildPairingRequestOptions,
): RelayPairingRequestPayload {
  const now = options.now ?? (() => Math.floor(Date.now() / 1000));
  return {
    request_id: options.requestId,
    public_key: options.identity.e2e_public_key,
    signing_public_key: options.identity.signing_public_key,
    ...(options.deviceName ? { device_name: options.deviceName } : {}),
    ...(options.platform ? { platform: options.platform } : {}),
    requested_at: now(),
  };
}

export type ParsedPairingResponse =
  | { kind: 'accepted'; accepted: RelayPairingAccepted }
  | { kind: 'denied'; reason: string }
  | { kind: 'invalid'; reason: string };

/**
 * Validate a `pair.begin.response` payload. Strict about daemon keys/device id
 * (bad values must never be stored: fail-closed) and tolerant about the
 * accepted spelling/optional request_id so minor server-iteration differences
 * still work. The request_id is checked when the server echoes it.
 */
export function parseRelayPairingResponse(
  payload: unknown,
  requestId: string,
  now: () => number = () => Math.floor(Date.now() / 1000),
): ParsedPairingResponse {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    return { kind: 'invalid', reason: '配对响应不是 JSON 对象' };
  }
  const record = payload as Record<string, unknown>;
  if (record.request_id !== undefined) {
    if (typeof record.request_id !== 'string' || record.request_id !== requestId) {
      return { kind: 'invalid', reason: '配对响应的 request_id 不匹配' };
    }
  }
  const status = typeof record.status === 'string' ? record.status.toLowerCase() : '';
  const accepted = record.accepted === true || status === 'accepted' || status === 'approved';
  if (!accepted) {
    if (record.accepted === false || status === 'denied' || status === 'rejected') {
      const reason =
        (typeof record.reason === 'string' && record.reason.trim()) ||
        (typeof record.message === 'string' && record.message.trim()) ||
        'PC 拒绝了本次配对请求';
      return { kind: 'denied', reason: reason.slice(0, MAX_REASON_LENGTH) };
    }
    return { kind: 'invalid', reason: '配对响应缺少 accepted 状态' };
  }
  const daemonDeviceId = normalizeDaemonDeviceId(record.daemon_device_id ?? record.device_id);
  const daemonPublicKey = normalizeDaemonE2ePublicKey(record.daemon_public_key);
  if (!daemonDeviceId) {
    return { kind: 'invalid', reason: '配对响应缺少有效的 daemon_device_id' };
  }
  if (!daemonPublicKey) {
    return {
      kind: 'invalid',
      reason: '配对响应缺少有效的 daemon_public_key（X25519，base64url 32 字节）',
    };
  }
  return {
    kind: 'accepted',
    accepted: {
      daemon_device_id: daemonDeviceId,
      daemon_public_key: daemonPublicKey,
      accepted_at: resolveAcceptedAt(record, now),
    },
  };
}

/**
 * Rejections that only mean "your request is already in flight / was already
 * decided" (daemon retry semantics). They must not flip the phone into the
 * denied state: keep waiting for the decision instead.
 */
export function isRetryablePairingRejection(reason: string): boolean {
  return /duplicate|already (pending|approved|decided|handled)|in flight/i.test(reason);
}

/** Server implementation (task-14) timestamps acceptances as `server_ts`. */
function resolveAcceptedAt(record: Record<string, unknown>, now: () => number): number {
  for (const key of ['accepted_at', 'server_ts'] as const) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return Math.floor(value);
  }
  return now();
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export interface RelayPairingSessionOptions {
  /** e.g. `wss://wintersrain.workers.dev/v1/ws` (same relay as live mode). */
  url: string;
  /** Device token from the completed Worker pairing. */
  token: string;
  /** Target daemon device id (`dev_…`), from the PC (Settings / approve CLI). */
  daemonDeviceId: string;
  identity: DeviceIdentityStored;
  deviceName?: string;
  platform?: string;
  timeoutMs?: number;
  requestIdFactory?: () => string;
  now?: () => number;
  relayOptions?: Partial<
    Pick<
      RelayClientOptions,
      | 'webSocketFactory'
      | 'WebSocketImpl'
      | 'scheduler'
      | 'random'
      | 'reconnect'
      | 'outboxLimit'
      | 'baseBackoffMs'
      | 'maxBackoffMs'
      | 'jitterMs'
    >
  >;
  onUpdate?: (view: RelayPairingView) => void;
  /** Fired once when the daemon accepted the bootstrap (save keys here). */
  onAccepted?: (accepted: RelayPairingAccepted) => void;
}

const IDLE_VIEW: RelayPairingView = { status: 'idle', retryable: false };
const globalTimers: RelayTimers = {
  setTimeout: (handler, ms) => setTimeout(handler, ms),
  clearTimeout: (handle) => clearTimeout(handle),
};

export class RelayPairingSession {
  private readonly options: RelayPairingSessionOptions;
  private readonly requestIdFactory: () => string;
  private readonly now: () => number;
  private readonly timeoutMs: number;
  private readonly timers: RelayTimers;
  private relay: RelayClient | null = null;
  private requestId: string | null = null;
  private requestPayload: RelayPairingRequestPayload | null = null;
  private timeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private sentForConnection = false;
  private disposed = false;
  private view: RelayPairingView = { ...IDLE_VIEW };

  constructor(options: RelayPairingSessionOptions) {
    this.options = options;
    this.requestIdFactory = options.requestIdFactory ?? (() => randomId('pair_', 12));
    this.now = options.now ?? (() => Math.floor(Date.now() / 1000));
    this.timeoutMs = Math.max(1000, options.timeoutMs ?? RELAY_PAIRING_DEFAULT_TIMEOUT_MS);
    this.timers = options.relayOptions?.scheduler ?? globalTimers;
  }

  get state(): RelayPairingView {
    return { ...this.view };
  }

  /** True while the user should keep the pending UI open. */
  get pending(): boolean {
    return this.view.status === 'connecting' || this.view.status === 'waiting';
  }

  /**
   * Validate preconditions and start (or resume) the bootstrap. Repeated calls
   * while a request is pending are idempotent (same request_id, no duplicate
   * send; the daemon side is expected to be idempotent per request_id too).
   */
  start(): RelayPairingView {
    if (this.disposed) {
      return this.emit({ status: 'error', error: '配对会话已关闭', retryable: false });
    }
    const configError = this.configurationError();
    if (configError) return this.emit({ status: 'error', error: configError, retryable: false });
    if (this.requestId && this.pending) {
      if (this.view.status === 'waiting' && !this.sentForConnection) this.sendBootstrap();
      return this.state;
    }
    this.requestId = this.requestIdFactory();
    this.requestPayload = buildRelayPairingRequest({
      identity: this.options.identity,
      requestId: this.requestId,
      deviceName: this.options.deviceName,
      platform: this.options.platform,
      now: this.now,
    });
    this.sentForConnection = false;
    this.emit({
      status: 'connecting',
      request_id: this.requestId,
      message: '正在通过 Worker 中继连接 PC daemon…',
      retryable: false,
    });
    const relay = this.ensureRelay();
    relay.connect();
    if (relay.status === 'connected') this.beginWaiting();
    return this.state;
  }

  /** Retry after denied/timeout/disconnect/error with a fresh request_id. */
  retry(): RelayPairingView {
    this.clearTimeoutTimer();
    this.requestId = null;
    this.requestPayload = null;
    this.sentForConnection = false;
    return this.start();
  }

  /** Stop waiting, close the bootstrap relay and reset to idle. */
  cancel(): void {
    this.clearTimeoutTimer();
    this.closeRelay();
    this.requestId = null;
    this.requestPayload = null;
    this.sentForConnection = false;
    this.emit({ ...IDLE_VIEW });
  }

  dispose(): void {
    this.disposed = true;
    this.clearTimeoutTimer();
    this.closeRelay();
  }

  // -- internals ------------------------------------------------------------

  private configurationError(): string | null {
    if (!this.options.url.trim()) return '未配置 Worker 中继地址（请先完成 Worker 配对）';
    if (!this.options.token.trim()) return '缺少设备 token（请先完成 Worker 配对）';
    if (!normalizeDaemonDeviceId(this.options.daemonDeviceId)) {
      return '缺少目标 daemon device id（请先在 PC 上运行 approve-relay-pairing.ts --identity 获取 dev_…）';
    }
    if (!this.options.identity?.e2e_public_key || !this.options.identity.signing_public_key) {
      return '本机设备密钥未初始化（请重新配对）';
    }
    return null;
  }

  private ensureRelay(): RelayClient {
    if (this.relay) return this.relay;
    const overrides = this.options.relayOptions ?? {};
    this.relay = new RelayClient({
      url: this.options.url,
      token: this.options.token,
      role: 'phone',
      reconnect: true,
      jitterMs: 0,
      ...overrides,
      onStatus: (status, detail) => this.handleRelayStatus(status, detail),
      onMessage: (frame) => this.handleMessageFrame(frame),
      onError: (error) => this.handleRelayError(error),
      onAuthError: (error) => this.handleAuthError(error),
    });
    return this.relay;
  }

  private beginWaiting(): void {
    if (!this.requestId || !this.requestPayload) return;
    if (this.view.status !== 'connecting' && this.view.status !== 'waiting') return;
    this.sentForConnection = false;
    this.emit({
      status: 'waiting',
      request_id: this.requestId,
      message: '等待 PC 端确认：请在本机运行 approve-relay-pairing.ts --approve <request_id>',
      retryable: false,
    });
    this.sendBootstrap();
    this.armTimeout();
  }

  private handleRelayStatus(status: RelayStatus, detail?: string): void {
    if (this.view.status === 'accepted' || this.view.status === 'idle' || this.disposed) return;
    if (status === 'connected') {
      this.beginWaiting();
      return;
    }
    if (status === 'connecting') {
      if (this.requestId && this.view.status !== 'connecting') {
        this.emit({
          status: 'connecting',
          request_id: this.requestId,
          message: '正在连接 Worker 中继…',
          retryable: false,
        });
      }
      return;
    }
    if ((status === 'offline' || status === 'error') && this.requestId) {
      this.emit({
        status: 'disconnected',
        request_id: this.requestId,
        message: `中继连接中断${detail ? `：${detail}` : ''}；重连后会自动重发同一引导请求`,
        retryable: true,
      });
    }
  }

  private handleRelayError(error: RelayClientError): void {
    if (this.view.status === 'accepted' || this.view.status === 'idle' || this.disposed) return;
    this.emit({
      status: 'error',
      ...(this.requestId ? { request_id: this.requestId } : {}),
      error: `${error.message}（${error.code}）`,
      retryable: true,
    });
  }

  private handleAuthError(error: RelayClientError): void {
    this.clearTimeoutTimer();
    this.emit({
      status: 'error',
      ...(this.requestId ? { request_id: this.requestId } : {}),
      error: `中继鉴权失败：${error.message}`,
      retryable: false,
    });
  }

  private handleMessageFrame(frame: MessageFrame): void {
    const envelope: Envelope = frame.envelope;
    if (!envelope || envelope.type !== RELAY_PAIRING_RESPONSE_TYPE) return;
    if (!this.requestId) return;
    if (this.view.status === 'accepted') return;
    if (envelope.ciphertext) {
      this.emit({
        status: 'error',
        request_id: this.requestId,
        error: '配对响应格式不支持：引导阶段应为明文响应',
        retryable: true,
      });
      return;
    }
    const parsed = parseRelayPairingResponse(envelope.payload, this.requestId, this.now);
    if (parsed.kind === 'invalid') {
      this.emit({
        status: 'error',
        request_id: this.requestId,
        error: parsed.reason,
        retryable: true,
      });
      return;
    }
    if (parsed.kind === 'denied') {
      if (isRetryablePairingRejection(parsed.reason)) {
        // Duplicate/in-flight retry while the owner has not decided yet: stay
        // waiting instead of showing a false denial.
        return;
      }
      this.clearTimeoutTimer();
      this.emit({
        status: 'denied',
        request_id: this.requestId,
        error: parsed.reason,
        retryable: true,
      });
      return;
    }
    this.clearTimeoutTimer();
    this.emit({
      status: 'accepted',
      request_id: this.requestId,
      message: 'PC 已确认配对：daemon 公钥已保存，live relay 就绪',
      retryable: false,
      daemon_device_id: parsed.accepted.daemon_device_id,
      daemon_public_key: parsed.accepted.daemon_public_key,
      accepted_at: parsed.accepted.accepted_at,
    });
    this.closeRelay();
    this.options.onAccepted?.(parsed.accepted);
  }

  private sendBootstrap(): void {
    if (!this.requestPayload || !this.requestId) return;
    const relay = this.ensureRelay();
    const envelope = makeEnvelope(RELAY_PAIRING_REQUEST_TYPE, {
      id: randomId('evt_'),
      ts: this.now(),
      // The daemon uses envelope.device_id as the request source (and later as
      // the device row id); the bootstrap frame is plaintext, so this id is the
      // only routing/identity hint it has until the owner approves locally.
      device_id: this.options.identity.device_id,
      payload: this.requestPayload,
    });
    if (!relay.relay(this.options.daemonDeviceId, envelope)) {
      this.emit({
        status: 'error',
        request_id: this.requestId,
        error: '中继拒绝了引导请求（信封无效）',
        retryable: true,
      });
      return;
    }
    this.sentForConnection = true;
  }

  private armTimeout(): void {
    this.clearTimeoutTimer();
    this.timeoutTimer = this.timers.setTimeout(() => {
      this.timeoutTimer = null;
      if (this.disposed) return;
      if (this.view.status !== 'waiting' && this.view.status !== 'disconnected') return;
      this.emit({
        status: 'timeout',
        ...(this.requestId ? { request_id: this.requestId } : {}),
        message: '等待 PC 确认超时；可重试（重试会生成新的 request_id）',
        retryable: true,
      });
    }, this.timeoutMs);
  }

  private clearTimeoutTimer(): void {
    if (this.timeoutTimer === null) return;
    this.timers.clearTimeout(this.timeoutTimer);
    this.timeoutTimer = null;
  }

  private closeRelay(): void {
    this.relay?.dispose();
    this.relay = null;
    this.sentForConnection = false;
  }

  private emit(view: RelayPairingView): RelayPairingView {
    this.view = view;
    this.options.onUpdate?.({ ...view });
    return this.state;
  }
}

/** Defensive helper: future additive Worker pairing field → daemon device id. */
export function daemonDeviceIdFromPairingResponse(response: unknown): string {
  if (typeof response !== 'object' || response === null) return '';
  const record = response as Record<string, unknown>;
  return normalizeDaemonDeviceId(
    record.daemon_device_id ?? record.daemonDeviceId ?? record.peer_device_id,
  );
}

/** Defensive helper: event payload used by daemon rejections (generic `event`). */
export function isPairingRejectionEvent(payload: unknown): payload is EventPayload {
  if (typeof payload !== 'object' || payload === null) return false;
  const record = payload as Record<string, unknown>;
  return typeof record.event === 'string' && record.event.startsWith('pair.begin.');
}
